import { useEffect, useRef } from 'react';

import { fetchLoginUserSummary } from '../../../application/auth/authSessionService';
import {
  readStoredUser,
  readStoredUserSummary,
  writeStoredUserSummary
} from '../../../infrastructure/session/browserAuthStore';

// Refetches me/summary whenever the active workspace id changes via URL
// navigation. Solves two failure modes documented in the foundation plan
// (Track B step B3):
//   - Newly-added memberships invisible until a hard reload (Layout's
//     mount-only fetch never re-runs for in-tab nav).
//   - `visible_sections` drift after a role change on the backend — the
//     cached summary lags until the user reloads.
//
// Fallback role: when the user switches workspaces via the sidebar
// toggle, the POST /team/workspace/activate/ response now carries the
// fresh me/summary (applyActivationSideEffects writes it before the
// next paint). In that case the cached summary already names the new
// workspace as active and includes its role/persona/visible_sections —
// this hook detects that and skips the redundant GET. It still fires
// for direct URL navigation (deep link, browser back/forward, copy/
// pasted URL) where no activate POST ran.
//
// Dedupes by the most-recent workspaceId so React StrictMode's
// double-invoke or rapid back/forward doesn't fire multiple parallel
// requests for the same workspace. Errors are swallowed deliberately —
// the cached summary stays usable; the next nav will retry.

type StoredSummaryShape = {
  workspace_context?: { active_workspace_id?: string | null };
  workspaceContext?: { active_workspace_id?: string | null };
  workspaces?: { id?: string }[];
  data?: {
    workspace_context?: { active_workspace_id?: string | null };
    workspaceContext?: { active_workspace_id?: string | null };
    workspaces?: { id?: string }[];
  };
};

const storedSummaryHasFreshWorkspace = (
  workspaceId: string | null
): boolean => {
  if (!workspaceId) return false;
  const summary = readStoredUserSummary<StoredSummaryShape>();
  if (!summary || typeof summary !== 'object') return false;

  const root = summary.data ?? summary;
  const context =
    (root as StoredSummaryShape).workspace_context ??
    (root as StoredSummaryShape).workspaceContext ??
    null;
  const activeMatches =
    context?.active_workspace_id != null &&
    String(context.active_workspace_id) === String(workspaceId);
  if (!activeMatches) return false;

  // The summary must also include this workspace's row in
  // workspaces[] for useWorkspaceVisibility to resolve role / persona /
  // visible_sections. If activeWorkspaceId matches but the row is
  // missing (e.g. stale cache after a backend role change), refetch.
  const workspaces = Array.isArray((root as StoredSummaryShape).workspaces)
    ? (root as StoredSummaryShape).workspaces
    : null;
  if (!workspaces) return false;
  return workspaces.some(
    (ws) => ws?.id != null && String(ws.id) === String(workspaceId)
  );
};

export const useEnsureFreshWorkspaceSummary = (
  workspaceId: string | null
): void => {
  const lastFetchedRef = useRef<string | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!workspaceId) return undefined;
    // Anonymous visitor (no stored user) — the me/summary endpoint
    // would 401 and the apiClient interceptor would redirect to
    // /identity/login. That breaks the public donate flow: an
    // unauthenticated visitor on a public donate route (/join/<id>,
    // /donate/recipients/<id>, etc.) never reaches the donate page.
    // The route guard already keeps RequireAuth off
    // for these routes (`requiresAuth: false`); this match short-
    // circuits the layout-level refresh so the same anonymous
    // visitor doesn't get bounced by a stray summary fetch.
    if (!readStoredUser()) return undefined;
    if (lastFetchedRef.current === workspaceId) return undefined;
    if (storedSummaryHasFreshWorkspace(workspaceId)) {
      // The activate response (or a recent summary write) already
      // landed the fresh workspace row + active_workspace_id. Skip the
      // redundant GET. Mark as "fetched" so we don't loop on the next
      // render.
      lastFetchedRef.current = workspaceId;
      return undefined;
    }

    let cancelled = false;
    lastFetchedRef.current = workspaceId;

    const run = async () => {
      try {
        const summary = await fetchLoginUserSummary();
        if (cancelled) return;
        if (summary) {
          writeStoredUserSummary(summary);
        }
      } catch {
        // Allow the next nav to retry; cached summary remains.
        if (lastFetchedRef.current === workspaceId) {
          lastFetchedRef.current = null;
        }
      } finally {
        inFlightRef.current = null;
      }
    };

    inFlightRef.current = run();

    return () => {
      cancelled = true;
    };
  }, [workspaceId]);
};
